import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Navbar, Footer } from "../components/Layout";
import { useNoIndex } from "../hooks/useNoIndex";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";

const VerifyEmail = () => {
  useNoIndex();
  const [params] = useSearchParams();
  const { user, refresh } = useAuth();
  const token = params.get("token") || "";
  const [state, setState] = useState("loading"); // loading | success | error | already
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) { setState("error"); setError("Lien invalide ou incomplet."); return; }
    api.post("/auth/verify-email", { token })
      .then((r) => {
        if (r.data.already_verified) setState("already");
        else setState("success");
        // refresh user object so banner disappears
        if (user) refresh();
      })
      .catch((err) => {
        setState("error");
        setError(err.response?.data?.detail || "Lien invalide ou expiré.");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="washi-bg min-h-screen" data-testid="verify-email-page">
      <Navbar />
      <main className="max-w-xl mx-auto px-6 sm:px-12 py-16">
        <div className="bg-white rounded-3xl border border-[#1A2A40]/10 shadow-brut-sm p-10 text-center">
          {state === "loading" && (
            <>
              <Loader2 className="w-12 h-12 mx-auto text-[#A0AEC0] animate-spin mb-4" />
              <p className="text-[#4A5568]">Validation en cours…</p>
            </>
          )}

          {(state === "success" || state === "already") && (
            <>
              <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-[#34D399]/15 flex items-center justify-center">
                <CheckCircle2 className="w-9 h-9 text-[#34D399]" />
              </div>
              <h1 className="font-display font-black text-3xl text-[#1A2A40] mb-3">
                {state === "already" ? "Email déjà confirmé" : "Email confirmé ! 🌸"}
              </h1>
              <p className="text-[#4A5568] mb-8 leading-relaxed">
                {state === "already"
                  ? "Ton adresse était déjà validée, rien à faire !"
                  : "Merci ! Ton compte est maintenant pleinement sécurisé."}
              </p>
              <Link
                to={user ? "/dashboard" : "/login"}
                data-testid="verify-success-cta"
                className="inline-flex items-center gap-2 bg-[#E94A3F] hover:bg-[#d83a2f] text-white font-bold py-3 px-6 rounded-xl"
              >
                {user ? "Retour au tableau de bord" : "Se connecter"} <ArrowRight className="w-4 h-4" />
              </Link>
            </>
          )}

          {state === "error" && (
            <>
              <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-[#E94A3F]/15 flex items-center justify-center">
                <XCircle className="w-9 h-9 text-[#E94A3F]" />
              </div>
              <h1 className="font-display font-black text-3xl text-[#1A2A40] mb-3">Lien invalide</h1>
              <p className="text-[#4A5568] mb-2 leading-relaxed">{error}</p>
              <p className="text-sm text-[#A0AEC0] mb-8">
                Les liens de validation sont valables 7 jours et à usage unique.
              </p>
              <Link
                to={user ? "/dashboard" : "/login"}
                data-testid="verify-error-cta"
                className="inline-flex items-center gap-2 bg-[#1A2A40] hover:bg-[#0d1825] text-white font-bold py-3 px-6 rounded-xl"
              >
                {user ? "Retour au tableau de bord" : "Se connecter"} <ArrowRight className="w-4 h-4" />
              </Link>
              {user && (
                <p className="text-xs text-[#A0AEC0] mt-4">
                  Tu pourras demander un nouveau lien depuis la bannière jaune en haut du tableau de bord.
                </p>
              )}
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default VerifyEmail;
